import { useEffect, useState } from "react";
import { ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CountryInput from "../../components/CountryInput";
import SignUpNavbar from "../../components/SignUpNavbar";
import { useAuth } from "../../context/AuthContext";
import useResolveGeo, { useSetResidence } from "../../hooks/useResolveGeo";

const SUPPORTED_ONBOARDING_COUNTRIES = ["NG", "US", "CA", "GB"];

const supportedCountry = (country?: string | null) =>
  country && SUPPORTED_ONBOARDING_COUNTRIES.includes(country) ? country : null;

const ConfirmResidence = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetch } = useAuth();
  const { detectedCountry, residenceCountry, isDeclared, isResolvingGeo } = useResolveGeo();
  const [country, setCountry] = useState(
    supportedCountry(residenceCountry) ?? supportedCountry(detectedCountry) ?? "NG",
  );
  const inSignup = location.pathname.startsWith("/sign-up/");
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  useEffect(() => {
    if (supportedCountry(residenceCountry)) setCountry(residenceCountry as string);
    else if (supportedCountry(detectedCountry)) setCountry(detectedCountry as string);
  }, [residenceCountry, detectedCountry]);

  useEffect(() => {
    if (inSignup && isDeclared && residenceCountry) {
      navigate(residenceCountry === "NG" ? "/sign-up/verify-nin" : "/sign-up/national-identity", { replace: true });
    }
  }, [inSignup, isDeclared, residenceCountry, navigate]);

  const { setResidence, savingResidence } = useSetResidence({
    async onSuccess() {
      await refetch();
      if (inSignup) navigate(country === "NG" ? "/sign-up/verify-nin" : "/sign-up/national-identity", { replace: true });
      else navigate(returnTo ?? "/aviation/international/v2/search", { replace: true });
    },
    onError(error) { toast.error(error?.message || "Could not save your country of residence"); },
  });

  return (
    <div>
      <ScrollRestoration />
      {inSignup && <SignUpNavbar />}
      <div className="flex flex-col justify-center items-center p-10 px-5 md:px-20 gap-8 min-h-[60vh]">
        <div className="text-center max-w-xl space-y-3">
          <h1 className="text-3xl lg:text-5xl font-bold text-deepBlue">Where do you live?</h1>
          <p className="text-gray-600">
            {supportedCountry(detectedCountry)
              ? `We think you live in ${detectedCountry}. Is that correct?`
              : "Choose your country of residence."}
          </p>
          <p className="text-sm text-gray-500">Location is only a suggestion. Your answer decides which KYC steps apply.</p>
        </div>
        <div className="w-full max-w-md flex flex-col gap-5">
          <CountryInput
            selected={country}
            onSelect={setCountry}
            allowedCountries={SUPPORTED_ONBOARDING_COUNTRIES}
          />
          <p className="text-sm text-gray-500">
            Onboarding is currently available in Nigeria, the United States, Canada, and the United Kingdom.
          </p>
          <button
            type="button"
            disabled={savingResidence || isResolvingGeo || !country}
            onClick={() => setResidence({ country })}
            className="bg-deepBlue text-white rounded-xl px-8 py-3 font-medium disabled:opacity-60"
          >
            {savingResidence ? "Saving..." : "Confirm and continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmResidence;
